import { useState, useEffect } from "react";
import { NavLink, useLocation, useNavigate } from "react-router-dom";
import { Sun, Calendar, Plus, FileText, Sparkles, Grid3x3 } from "lucide-react";
import { api } from "../lib/api";
import Tip from "./Tip";
import QuickAddSheet from "./QuickAddSheet";
import { useApp } from "../lib/AppContext";

/**
 * Bottom Navigation Bar mobile — 6 tab fisse.
 * Visibile solo < md (768px). Rispetta safe-area-inset-bottom (iPhone notch/home indicator).
 * Nascosta quando è aperta la tastiera virtuale iOS (VisualViewport API).
 */
export default function MobileTabBar({ onOpenChatbot }) {
  const [quickOpen, setQuickOpen] = useState(false);
  const [badges, setBadges] = useState({ oggi: 0, agenda: 0, quick_access: 0, ai_assistant: 0, altro: 0 });
  const [kbVisible, setKbVisible] = useState(false);
  const nav = useNavigate();
  const loc = useLocation();
  const { user } = useApp();

  // Poll badge counts ogni 60s + on route change
  useEffect(() => {
    if (!user) return;
    let cancel = false;
    const load = async () => {
      try {
        const b = await api.mobileBadges();
        if (!cancel) setBadges(b);
      } catch {}
    };
    load();
    const iv = setInterval(load, 60000);
    return () => { cancel = true; clearInterval(iv); };
  }, [user, loc.pathname]);

  // Rileva tastiera iOS via VisualViewport API
  useEffect(() => {
    if (typeof window === "undefined" || !window.visualViewport) return;
    const vv = window.visualViewport;
    const check = () => {
      const ratio = vv.height / window.innerHeight;
      setKbVisible(ratio < 0.75);
    };
    check();
    vv.addEventListener("resize", check);
    return () => vv.removeEventListener("resize", check);
  }, []);

  if (kbVisible) return null;

  return (
    <>
      <nav
        className="fixed bottom-0 inset-x-0 z-40 border-t bg-white md:hidden"
        style={{
          borderColor: "#E5E7EB",
          paddingBottom: "env(safe-area-inset-bottom)",
          boxShadow: "0 -4px 16px rgba(0,0,0,0.04)",
        }}
        role="tablist"
        aria-label="Navigazione principale mobile"
        data-testid="mobile-tab-bar"
      >
        <div className="grid grid-cols-6 items-end h-14 relative">
          <TabItem
            to="/agenda?view=day"
            label="Oggi"
            icon={Sun}
            badge={badges.oggi}
            active={(() => {
              if (loc.pathname !== "/agenda") return false;
              const v = new URLSearchParams(loc.search).get("view");
              return v === "day" || v == null;
            })()}
            testid="tabbar-oggi"
            onSecondTap={() => {
              // 2° tap sul tab "Oggi" già attivo → resetta ref a oggi (day
              // view). Utile quando Piera ha navigato indietro nei giorni
              // dall'agenda e vuole tornare al giorno corrente senza dover
              // pescare il pulsante "Oggi" in cima alla pagina.
              nav("/agenda?view=day", { replace: false });
            }}
          />
          <TabItem
            to="/agenda?view=week"
            label="Agenda"
            icon={Calendar}
            badge={badges.agenda}
            active={(() => {
              // "Agenda" is active for any Agenda page that is NOT the "day"
              // view (day is owned by the Oggi tab). This includes /agenda
              // without params (default view=day is handled by Oggi tab) and
              // covers /agenda?view=week|month|year — so a 2nd tap correctly
              // resets the ref to the current period WITHOUT switching view.
              if (loc.pathname !== "/agenda") return false;
              const v = new URLSearchParams(loc.search).get("view");
              return v === "week" || v === "month" || v === "year";
            })()}
            testid="tabbar-agenda"
            onSecondTap={() => {
              // 2° tap sul tab "Agenda" → torna al periodo corrente
              // MANTENENDO la vista attuale (settimana, mese o anno).
              const currentView = new URLSearchParams(loc.search).get("view") || "week";
              nav(`/agenda?view=${currentView}`, { replace: false });
            }}
          />
          {/* Central + button */}
          <div className="flex justify-center" role="none">
            <Tip label="Aggiungi rapidamente" side="top">
              <button
                type="button"
                onClick={() => setQuickOpen(true)}
                className="-mt-5 h-12 w-12 rounded-full grid place-items-center shadow-lg transition active:scale-95"
                style={{ background: "var(--pxos-primary-accent)", color: "white" }}
                data-testid="tabbar-add"
                aria-label="Aggiungi (apri pannello rapido)"
              >
                <Plus size={22} strokeWidth={2.5} />
              </button>
            </Tip>
          </div>
          <TabItem
            to="/documenti"
            label="Rapido"
            icon={FileText}
            badge={badges.quick_access}
            active={loc.pathname.startsWith("/documenti")}
            testid="tabbar-quick"
          />
          <ActionItem
            label="Segretaria"
            icon={Sparkles}
            badge={badges.ai_assistant}
            onClick={onOpenChatbot}
            testid="tabbar-ai"
            ariaLabel="Apri la Segretaria IA"
          />
          <TabItem
            to="/menu"
            label="Altro"
            icon={Grid3x3}
            badge={badges.altro}
            active={loc.pathname === "/menu"}
            testid="tabbar-more"
          />
        </div>
      </nav>
      <QuickAddSheet open={quickOpen} onClose={() => setQuickOpen(false)} onNavigate={(path) => { setQuickOpen(false); nav(path); }} />
    </>
  );
}

function TabItem({ to, label, icon: Icon, badge = 0, active = false, testid, onSecondTap }) {
  // If the user taps a tab that is already active, we treat it as a
  // "reset" gesture: call `onSecondTap` (if provided) instead of letting
  // the router process the same URL again. This is Piera's iOS-style
  // "2nd tap → jump to Today" behavior.
  const onClick = (e) => {
    if (active && typeof onSecondTap === "function") {
      e.preventDefault();
      onSecondTap();
    }
  };
  return (
    <NavLink
      to={to}
      onClick={onClick}
      className="flex flex-col items-center justify-end gap-0.5 pt-1.5 pb-1 min-h-[44px] relative"
      role="tab"
      aria-selected={active}
      data-testid={testid}
    >
      <div className="relative">
        <Icon
          size={22}
          style={{ color: active ? "var(--pxos-primary-accent)" : "var(--pxos-text-dark)", opacity: active ? 1 : 0.6 }}
        />
        {badge > 0 && <BadgeDot count={badge} />}
      </div>
      <span
        className="text-[11px] leading-none tracking-tight"
        style={{
          color: active ? "var(--pxos-primary-accent)" : "var(--pxos-text-dark)",
          opacity: active ? 1 : 0.7,
          fontWeight: active ? 700 : 500,
        }}
      >
        {label}
      </span>
      {active && (
        <span
          className="absolute top-0 left-1/2 -translate-x-1/2 h-0.5 w-8 rounded-full"
          style={{ background: "var(--pxos-primary-accent)" }}
          aria-hidden="true"
        />
      )}
    </NavLink>
  );
}

function ActionItem({ label, icon: Icon, badge = 0, onClick, testid, ariaLabel }) {
  return (
    <button
      type="button"
      onClick={onClick}
      className="flex flex-col items-center justify-end gap-0.5 pt-1.5 pb-1 min-h-[44px] relative"
      role="tab"
      aria-label={ariaLabel}
      data-testid={testid}
    >
      <div className="relative">
        <Icon size={22} style={{ color: "var(--pxos-text-dark)", opacity: 0.7 }} />
        {badge > 0 && <BadgeDot count={badge} />}
      </div>
      <span className="text-[11px] leading-none tracking-tight font-medium" style={{ color: "var(--pxos-text-dark)", opacity: 0.7 }}>
        {label}
      </span>
    </button>
  );
}

function BadgeDot({ count }) {
  const display = count > 9 ? "9+" : String(count);
  return (
    <span
      className="absolute -top-1.5 -right-2 min-w-[16px] h-4 px-1 rounded-full bg-red-500 text-white text-[10px] font-bold grid place-items-center leading-none"
      aria-label={`${count} elementi in attesa`}
    >
      {display}
    </span>
  );
}
